/** @format */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { WidthProvider, Responsive, getBreakpointFromWidth } from 'react-grid-layout';
import { Container } from '../container';
import { GetWidget } from '../widget';
import Loader from 'src/components/Loader';
import { isMobile, isMobileOnly, isTablet } from 'react-device-detect';
// api
import { useDispatch, useSelector } from 'src/store';
import { getWidgetInfo } from 'src/slices/users';
import useAuth from 'src/hooks/useAuth';
import { Dialog, DialogContent, Grid } from '@material-ui/core';
import _ from 'lodash';
import DummyWidget from './widgetDummy';
import { useTranslation } from 'react-i18next';
import { calcSize, calculateItemPositions, getBreakpoint } from 'src/utils/widget';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

let totalWidthOfItems = 0;
const changeTotalWidthOfItems = data => {
  totalWidthOfItems += data;
};
const generateLayout = (item, index, totalWidthOfItems, changeTotalWidthOfItems, itemPositions) => {
  const { size } = item;
  changeTotalWidthOfItems(calcSize(size, item).w);
  const layoutRenerated = {
    i: item.i || index + '',
    x: itemPositions[index] !== null ? itemPositions[index].x : item.x != null ? item.x : totalWidthOfItems % 12,
    y:
      itemPositions[index] !== null
        ? itemPositions[index].y
        : item.y != null
          ? item.y
          : Math.floor(totalWidthOfItems / 12),
    ...calcSize(size, item)
  };
  return layoutRenerated;
};

const ROW_HEIGHT = isMobileOnly ? 92 : 36;
const ROW_MARGIN = 32;
const GRID_CONFIGS = {
  compactType: `vertical`,
  className: `widgetlayout`,
  rowHeight: ROW_HEIGHT,
  breakpoints: { xxl: 1919, xl: 1439, lg: 1023, md: 767, sm: 575, xs: 0 },
  cols: { xxl: 12, xl: 12, lg: 12, md: 2, sm: 2, xs: 1 },
  margin: [ROW_MARGIN, ROW_MARGIN],
  containerPadding: [16, 0],
  isDraggable: false,
  isResizable: false
};

const debouncedUpdate = _.debounce(callback => {
  callback();
}, 100);

export const WidgetGrid = ({ dashboardInfo }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { widgetInfo } = dashboardInfo ? { widgetInfo: dashboardInfo } : useSelector(state => state.users);
  // const [openDialog, setOpenDialog] = useState(false);
  const [globalLayouts, setLayouts] = useState({});
  const [itemPositions, setItemPositions] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [currentBreakPoint, setCurrentBreakPoint] = useState(null);
  const [layoutStable, setLayoutStable] = useState(false);
  const gridLayoutRef = useRef(null);
  const currentSize = useRef([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [visible, setVisible] = useState(false);

  // avoid flash of unstyled content
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user && widgetInfo && widgetInfo.length === 0) {
      dispatch(getWidgetInfo());
    }
  }, [user, widgetInfo, dispatch]);

  useEffect(() => {
    if (user && widgets && widgets.length === 0) {
      widgetInfo ? setWidgets(widgetInfo.data || []) : setWidgets([]);
    }
  }, [user, widgetInfo]);

  useEffect(() => {
    if (!widgets.length) return;

    setItemPositions(calculateItemPositions(widgets, totalWidthOfItems));

    if (itemPositions.length) {
      const layoutArr = [];
      const newWigets = [];

      widgets.forEach((item, index) => {
        const dataGrid = generateLayout(item, index, totalWidthOfItems, changeTotalWidthOfItems, itemPositions);
        layoutArr.push(dataGrid);
        newWigets.push({
          ...item,
          i: dataGrid.i,
          x: dataGrid.x,
          y: dataGrid.y,
          w: dataGrid.w,
          minW: dataGrid.minW,
          maxW: dataGrid.maxW,
          h: dataGrid.h,
          minH: dataGrid.minH,
          maxH: dataGrid.maxH
        });
      });

      setWidgets(newWigets);
      if (currentBreakPoint) {
        setLayouts({ ...globalLayouts, [currentBreakPoint]: layoutArr });
      } else {
        setLayouts({ ...globalLayouts, [Object.keys(globalLayouts)[0]]: layoutArr });
      }
    }
  }, [widgets.length, itemPositions.length, currentBreakPoint]);

  useEffect(() => {
    if (lastUpdate && lastUpdate.widgets && lastUpdate.layouts) {
      setWidgets(lastUpdate.widgets);
      setLayouts({
        ...lastUpdate.layouts.layouts,
        [lastUpdate.layouts.breakpoint]: lastUpdate.layouts.newLayout
      });
    }
  }, [lastUpdate]);

  const onLayoutChange = (currentLayout, allLayouts) => {
    setLayouts(allLayouts);
    setLayoutStable(false);
    debouncedUpdate(() => {
      setLayoutStable(true);
      if (gridLayoutRef.current) gridLayoutRef.current.classList.add('dashboard-widgets-mobile');
    });
  };

  const onBreakpointChange = (newBreakpoint, newCols) => {
    setCurrentBreakPoint(newBreakpoint);
  };

  const layoutConfig = useMemo(
    () => ({
      layouts: globalLayouts,
      onLayoutChange,
      onBreakpointChange,
      autoSize: true,
      ...GRID_CONFIGS
    }),
    [globalLayouts, onLayoutChange, onBreakpointChange]
  );

  const handleItemSizeChange = useCallback(
    (height, i) => {
      if (Object.keys(globalLayouts).length === 0 || !currentBreakPoint) return;

      let newH = Math.ceil(height / (ROW_HEIGHT + ROW_MARGIN / 2)) + 0.5;
      if (currentSize.current[i] === newH) return;

      debouncedUpdate(() => {
        const currentWidgets = lastUpdate?.widgets || widgets;
        const currentLayouts = { ...globalLayouts };

        const newLayoutBreakpoint = currentLayouts[currentBreakPoint].map((item, index) =>
          index === i ? { ...item, h: newH } : item
        );

        const newWidgets = currentWidgets.map((item, index) => {
          if (index === i) return { ...item, h: newH };
          const existingLayout = currentLayouts[currentBreakPoint][index];
          return { ...item, h: existingLayout.h };
        });

        setLastUpdate({
          layouts: {
            layouts: currentLayouts,
            breakpoint: currentBreakPoint,
            newLayout: newLayoutBreakpoint
          },
          widgets: newWidgets
        });

        currentSize.current[i] = newH;
      });
    },
    [globalLayouts, currentBreakPoint]
  );

  return (
    <div
      className="container-grid"
      style={{
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.25s ease-out'
      }}
    >
      <div className="dashboard-widgets-outer">
        {/* {!IS_PRODUCTION && (
          <button className="dashboard-config-button" onClick={() => setOpenDialog(true)}>
            Dashboard konfigurieren
          </button>
        )} */}
        {widgets.length > 0 ? (
          <div className={`dashboard-widgets`} ref={gridLayoutRef}>
            <ResponsiveReactGridLayout {...layoutConfig}>
              {widgets.map((item, index, items) => {
                return (
                  <div key={index} data-size={item.size}>
                    <GetWidget
                      globalLayouts={globalLayouts}
                      currentBreakPoint={currentBreakPoint}
                      data={item}
                      onSizeChange={height => handleItemSizeChange(height, index)}
                    />
                  </div>
                );
              })}
            </ResponsiveReactGridLayout>
          </div>
        ) : (
          <Loader loading={true} />
        )}

        {/* <Dialog
          fullWidth
          maxWidth="xl"
          height={500}
          open={openDialog}
          PaperProps={{
            style: {
              backgroundColor: '#fff',
              borderRadius: 10
            }
          }}
          onClose={() => setOpenDialog(false)}
          aria-labelledby="max-width-dialog-title"
        >
          <DummyWidget
            widgetInfo={widgetInfo}
            defaultProps={GRID_CONFIGS}
            compactType={'vertical'}
            lang="en"
            layouts={globalLayouts}
            setOpenDialog={setOpenDialog}
          />
        </Dialog> */}
      </div>
    </div>
  );
};
